import React, { useRef, useEffect } from 'react';

/* ══════════════════════════════════════════════════════════════
   OTP digit boxes

   Extracted from the password-reset screen so signup verification
   doesn't get a second, subtly-different copy of the same fiddly
   focus handling. Both flows now share one behaviour:

     - typing advances, backspace on an empty box retreats
     - arrow keys move between boxes
     - pasting a whole code fans it out across the boxes
     - filling the last box fires onComplete, so the common case
       needs no button press

   Controlled by a plain string so the parent never has to think
   about the per-box array.
══════════════════════════════════════════════════════════════ */

export default function OtpInput({
  value,
  onChange,
  length = 6,
  onComplete,
  autoFocus = true,
  disabled = false,
  label = 'Verification code',
}) {
  const refs = useRef([]);

  const digits = Array.from({ length }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const emit = next => {
    const joined = next.join('').slice(0, length);
    onChange(joined);
    if (joined.length === length) onComplete?.(joined);
  };

  const setDigit = (i, raw) => {
    const clean = raw.replace(/\D/g, '');
    const next = [...digits];

    if (!clean) {
      next[i] = '';
      emit(next);
      return;
    }

    // typing or dropping several digits at once fans out from here
    clean.split('').forEach((ch, offset) => {
      if (i + offset < length) next[i + offset] = ch;
    });
    emit(next);
    refs.current[Math.min(i + clean.length, length - 1)]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
  };

  const onPaste = e => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    e.preventDefault();
    const next = Array(length).fill('');
    text.split('').forEach((ch, i) => { next[i] = ch; });
    emit(next);
    refs.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <div
      style={{ display: 'flex', gap: 8, justifyContent: 'center' }}
      onPaste={onPaste}
      role="group"
      aria-label={label}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => { refs.current[i] = el; }}
          className="otp-box"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          value={d}
          disabled={disabled}
          onChange={e => setDigit(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
